/**
 * <cart-drawer-recommendations>
 *
 * Loads product recommendations based on the first item currently in the
 * cart and renders them inside the cart drawer using the theme's native
 * product card (same card used on the products collection page), fetched
 * via Shopify's Section Rendering API through the small fragment section
 * exposed by `recently-viewed-list.js`. Re-fetches whenever the cart is
 * refreshed so the recommendations update as the cart changes.
 */

import { Component } from '@theme/component';
import { fetchProductCardHTML } from '@theme/recently-viewed-list';

class CartDrawerRecommendations extends Component {
  #lastSeedProductId = '';

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener('cart:refresh', this.#render);
    document.addEventListener('cart:update', this.#render);
    document.addEventListener('discount:update', this.#render);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:refresh', this.#render);
    document.removeEventListener('cart:update', this.#render);
    document.removeEventListener('discount:update', this.#render);
  }

  /**
   * Called by the theme's morph helper after the cart drawer section is
   * re-rendered (for example after a discount is applied). The subtree of
   * this element is preserved via `data-skip-subtree-update` so any cards
   * we already rendered stay in place, but if the first cart item changed
   * the seed product id attribute will have been updated and we should
   * re-fetch recommendations.
   */
  updatedCallback() {
    super.updatedCallback?.();
    const currentSeed = this.#seedProductId;
    if (currentSeed !== this.#lastSeedProductId) {
      this.#render();
    }
  }

  get #limit() {
    return Number(this.dataset.limit || 4);
  }

  get #seedProductId() {
    return this.dataset.seedProductId || '';
  }

  get #recommendationsUrl() {
    return this.dataset.recommendationsUrl || '/recommendations/products';
  }

  #render = async () => {
    const listEl = this.refs.list;
    if (!(listEl instanceof HTMLElement)) return;

    const seedId = this.#seedProductId;
    this.#lastSeedProductId = seedId;
    if (!seedId) {
      this.#hide();
      return;
    }

    const url = new URL(this.#recommendationsUrl, window.location.origin);
    url.searchParams.set('product_id', seedId);
    url.searchParams.set('limit', String(this.#limit));
    url.searchParams.set('intent', 'related');

    let products = [];
    try {
      const response = await fetch(`${url.pathname}${url.search}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        this.#hide();
        return;
      }
      const data = await response.json();
      products = Array.isArray(data?.products) ? data.products : [];
    } catch (_error) {
      this.#hide();
      return;
    }

    if (products.length === 0) {
      this.#hide();
      return;
    }

    const handles = products.map((product) => product?.handle).filter(Boolean);
    const cards = await Promise.all(handles.map(fetchProductCardHTML));
    const html = cards.filter(Boolean).join('');

    if (!html.trim()) {
      this.#hide();
      return;
    }

    this.removeAttribute('data-empty');
    listEl.innerHTML = html;
  };

  #hide() {
    this.setAttribute('data-empty', 'true');
    const listEl = this.refs.list;
    if (listEl instanceof HTMLElement) listEl.innerHTML = '';
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
